import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { StorageService } from '../../services/storage-service';
import { AppUrl } from '../../appUrl';
import { TranslateModule } from '../../shareds/translate/translate.module';
import { TranslateService } from '../../shareds/translate/translate.service';
@Component({
  selector: 'app-customer-info',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './customer-info.component.html',
  styleUrls: ['./customer-info.component.css']
})
export class CustomerInfoComponent implements OnInit {
  private router = inject(Router);
  private ac = inject(ActivatedRoute);
  private st = inject(StorageService);
  private http = inject(HttpClient);
  private translate = inject(TranslateService);

  form = {
    name: '',
    email: '',
    phoneCode: 'TH',
    phone: '',
    notMainGuest: false,
    mainGuestName: '',
  };

  errors: Record<string, string> = {};

  private validate(): boolean {
    const e: Record<string, string> = {};
    if (!this.form.name.trim()) e['name'] = this.translate.instant('กรุณากรอกชื่อ - นามสกุล', 'customer-info');
    if (!this.form.email.trim()) {
      e['email'] = this.translate.instant('กรุณากรอกอีเมล', 'customer-info');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) {
      e['email'] = this.translate.instant('รูปแบบอีเมลไม่ถูกต้อง', 'customer-info');
    }
    if (!this.form.phoneCode) e['phoneCode'] = this.translate.instant('กรุณาเลือกประเทศ', 'customer-info');
    if (!this.form.phone.trim()) e['phone'] = this.translate.instant('กรุณากรอกเบอร์โทรศัพท์', 'customer-info');
    if (this.form.notMainGuest && !this.form.mainGuestName.trim()) {
      e['mainGuestName'] = this.translate.instant('กรุณาระบุชื่อผู้เข้าพักหลัก', 'customer-info');
    }
    this.errors = e;
    return Object.keys(e).length === 0;
  }

  clearError(field: string) {
    if (this.errors[field]) {
      const e = { ...this.errors };
      delete e[field];
      this.errors = e;
    }
  }

  countries = signal<any[]>([]);
  countriesLoading = signal(false);
  showCountryDropdown = signal(false);
  countrySearch = signal('');
  phoneCodeSig = signal('');

  countryOptions = computed(() =>
    this.countries().map(c => ({
      label: `(${c.phone ?? ''}) ${c.name ?? ''}`,
      code: c.id ?? '',
      phone: c.phone ?? '',
      name: c.name ?? '',
    }))
  );

  filteredCountryOptions = computed(() => {
    const q = this.countrySearch().toLowerCase().trim();
    if (!q) return this.countryOptions();
    return this.countryOptions().filter(o =>
      o.name.toLowerCase().includes(q) || String(o.phone).includes(q)
    );
  });

  selectedCountryOption = computed(() =>
    this.countryOptions().find(o => o.code === this.phoneCodeSig()) ?? null
  );

  selectCountry(opt: { code: string; phone: string; name: string }) {
    this.form.phoneCode = opt.code;
    this.phoneCodeSig.set(opt.code);
    this.showCountryDropdown.set(false);
    this.countrySearch.set('');
    this.clearError('phoneCode');
  }

  bookingData = signal<any>({ totalRooms: 0, totalNights: 1, totalPrice: 0 });
  profile = signal<any>(null);
  filledFromProfile = signal(false);

  ngOnInit() {
    const bd = this.st.getSession('bookingData');
    if (bd) this.bookingData.set(JSON.parse(bd));

    const ci = this.st.getSession('customerInfo');
    if (ci) {
      Object.assign(this.form, JSON.parse(ci));
      this.phoneCodeSig.set(this.form.phoneCode);
      this.loadCountries();
    } else {
      // ยังไม่มีข้อมูลใน session → ดึงจาก profile
      this.loadProfile();
      this.loadCountries();
    }
  }

  loadProfile() {
    this.http.get<any>('/api/Public/AuthHotel').subscribe({
      next: (res) => {
        const p = res?.data ?? res ?? null;
        if (!p) return;
        this.profile.set(p);

        const firstname = p.firstname ?? '';
        const lastname = p.lastname ?? '';
        const fullName = [firstname, lastname].filter(Boolean).join(' ') || (p.name ?? p.fullName ?? '');
        const email = p.username ?? p.email ?? '';
        const phone = p.phoneNumber ?? p.phone ?? p.mobile ?? '';
        const phoneCode = p.phoneCode ?? '';

        let filled = false;
        if (fullName) { this.form.name = fullName; filled = true; }
        if (email) { this.form.email = email; filled = true; }
        if (phone) { this.form.phone = phone; filled = true; }
        if (phoneCode) {
          if (phoneCode === '+66') {
            this.form.phoneCode = 'TH';
            this.phoneCodeSig.set('TH');
          }
          else {
            this.form.phoneCode = phoneCode;
            this.phoneCodeSig.set(phoneCode);
          }
          filled = true;
        }
        if (filled) this.filledFromProfile.set(true);
      },
      error: () => { }
    });
  }

  loadCountries() {
    this.countriesLoading.set(true);
    this.http.get<any>('/api/Setup/CountryCode').subscribe({
      next: (res) => {
        const list: any[] = res?.data ?? [];
        this.countries.set(list);
        this.countriesLoading.set(false);
        // ตั้งค่า default เป็นไทย ถ้ายังไม่มีค่า
        if (!this.form.phoneCode && list.length > 0) {
          const th = list.find((c: any) => String(c.phone ?? '').includes('66'));
          const def = th ?? list[0];
          this.form.phoneCode = def.id ?? '';
          this.phoneCodeSig.set(this.form.phoneCode);
        } else {
          this.phoneCodeSig.set(this.form.phoneCode);
        }
      },
      error: () => this.countriesLoading.set(false),
    });
  }

  toBack() {
    this.router.navigate(['/', AppUrl.list_room, this.ac.snapshot.params['app-id']]);
  }

  toNextStep() {
    if (!this.validate()) return;
    this.st.setSession('customerInfo', JSON.stringify(this.form));
    this.router.navigate(['/', AppUrl.bookign_info, this.ac.snapshot.params['app-id']]);
  }
}
